/**
 * Joy Basket — imagery system
 *
 * Single source of truth for every editorial photograph used across the app.
 * Curation rules (apply to every entry):
 *
 *   1. Warm natural light only — window light, golden hour. No flash, no
 *      cool-toned hospital lighting, no studio whiteboxes.
 *   2. Real moments. No posed smiles, no model-perfect parents, no babies
 *      looking at the camera with novelty hats. Quiet, in-between.
 *   3. Close, intimate crops. Often a detail — a hand, a foot, the edge of
 *      a blanket. Distance reads stocky.
 *   4. A warm palette per image — creams, oat, dust, terracotta, soft sage.
 *      Cool blues and saturated pinks are out (clichéd "boy/girl" coding).
 *   5. Composition allows a quote / headline overlay without fighting it —
 *      generous negative space on at least one side.
 *
 * When commissioned photography arrives, only this file changes.
 * Each entry is shaped:
 *   { src, alt, intent, mood, where }
 *
 * For a fuller per-section playbook, see frontend/src/lib/IMAGES.md.
 */

const u = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=85`;

/* Verified-stable photo used as the visual fallback when any other image
 * 404s — keeps the layout warm rather than showing alt-text. */
const FALLBACK_SRC = u('1555252333-9f8e92e65df9', 1200);

/* -------------------------------------------------------------------------- */
/*  HOME                                                                       */
/* -------------------------------------------------------------------------- */

/* A small hand resting on a soft cream blanket — close, intimate crop,
 * morning window light. Reverted from a cradle-scene swap because the cradle
 * read too retail; this detail shot anchors the brand's quiet, in-between
 * tone better even if it's more abstract than the headline. */
export const HOME_HERO = {
  src: u('1555252333-9f8e92e65df9', 1300),
  alt: 'A small hand resting on a soft cream blanket, lit by morning light',
  intent: 'A quiet detail. Anticipation, not announcement.',
  mood: 'calm, soft, intimate',
  where: 'Home — hero right column',
};

/* -------------------------------------------------------------------------- */
/*  STAGES (BabyRegistryGuide + RegistryOnboarding side panel)                 */
/* -------------------------------------------------------------------------- */

export const STAGE_EXPECTING = {
  src: u('1518495973542-4542c06a5843', 1100),
  alt: 'A pregnant figure cradled in soft cream linen, side window light',
  intent: 'Quiet anticipation. The basket as a private, unhurried place.',
  mood: 'private, unhurried',
  where: 'BabyRegistryGuide — Expecting · RegistryOnboarding — early steps',
};

export const STAGE_NEWBORN = {
  src: u('1519689680058-324335c77eba', 1100),
  alt: 'A newborn cradled close, soft natural light',
  intent: 'The first weeks. Closeness, not performance.',
  mood: 'close, hushed',
  where: 'BabyRegistryGuide — Newborn · RegistryOnboarding — later steps',
};

export const STAGE_FIRST_YEAR = {
  src: u('1471286174890-9c112ffca5b4', 1100),
  alt: 'A small child in warm-toned knitwear, taking unsteady steps',
  intent: 'The basket grows with them. Movement, not milestones.',
  mood: 'tender, in-motion',
  where: 'BabyRegistryGuide — First year & beyond',
};

/* -------------------------------------------------------------------------- */
/*  HOW IT WORKS                                                               */
/* -------------------------------------------------------------------------- */

/* The hero is the only photo on HowItWorks now — the three steps are
 * icon-led (PenLine / Link2 / Gift) for visual consistency with the
 * homepage's "How it works" section. */
export const HIW_HERO = {
  src: u('1502086223501-7ea6ecd79368', 1500),
  alt: 'A parent holding a tiny hand against a knitted blanket',
  intent: 'The reassurance the page is about to deliver.',
  mood: 'reassuring, close',
  where: 'How it works — hero',
};

/* -------------------------------------------------------------------------- */
/*  EMPTY STATES — image-aware variants                                        */
/* -------------------------------------------------------------------------- */

export const EMPTY_BASKET = {
  src: u('1490818387583-1baba5e638af', 900),
  alt: 'A warm-lit interior corner with cream textiles, waiting',
  intent: "Nothing here yet — but the container is ready.",
  mood: 'inviting, patient',
  where: 'Empty states — registry has no items',
};

export const NO_REGISTRIES_FOUND = {
  src: u('1543269865-cbf427effbad', 900),
  alt: 'A page in a blank notebook',
  intent: 'Nothing matched the search — try a different name.',
  mood: 'pause, neutral',
  where: 'Search — no results',
};

/* -------------------------------------------------------------------------- */
/*  REGISTRY SHARE / SOCIAL                                                    */
/* -------------------------------------------------------------------------- */

export const REGISTRY_OG = {
  src: u('1502086223501-7ea6ecd79368', 1600),
  alt: 'A parent holding a tiny hand',
  intent: 'OG fallback for shared registry links.',
  mood: 'tender, shareable',
  where: 'OG / share preview',
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Spread on <img> to apply src + alt + a graceful onError fallback.
 *
 * If an Unsplash ID 404s (which happens when photographers delete their
 * uploads), we silently swap to a known-stable warm photo rather than
 * letting the broken-image alt-text become visible. The data attribute
 * prevents a fallback loop in the unlikely case the fallback also fails.
 */
export const imgProps = (entry) =>
  entry
    ? {
        src: entry.src,
        alt: entry.alt,
        onError: (e) => {
          if (e.currentTarget.dataset.fallback === '1') return;
          e.currentTarget.dataset.fallback = '1';
          e.currentTarget.src = FALLBACK_SRC;
        },
      }
    : { src: '', alt: '' };
